import { Phone, Clock, MapPin, Navigation } from 'lucide-react';

export default function ContactSection() {
  const ADDRESS = '4417 Jackson Street, Marianna, Florida 32448';
  const MAP_EMBED = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3428.060201058133!2d-85.23046372441803!3d30.772889774564902!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x889321e19ea75c09%3A0x57a1752bd0e122d5!2sCave%20City%20Pawn!5e0!3m2!1sen!2sus!4v1770997290311!5m2!1sen!2sus';
  const DIRECTIONS_URL = 'https://www.google.com/maps/dir/?api=1&destination=4417+Jackson+Street,+Marianna,+FL+32448';

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-28 bg-dirt-brown noise-overlay torch-glow"
      aria-label="Contact Cave City Pawn Shop"
    >
      {/* Top wave */}
      <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute -top-1 left-0 w-full">
        <path d="M0 0L60 12C120 24 240 48 360 54C480 60 600 48 720 42C840 36 960 36 1080 42C1200 48 1320 60 1380 66L1440 72V0H0Z" fill="#D3C6A9"/>
      </svg>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-sm font-body font-semibold text-accent uppercase tracking-widest mb-3">We are Here to Help</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading text-bone-white uppercase mb-5 chisel-heading">
            Get In <span className="text-accent">Touch</span>
          </h2>
          <p className="text-base md:text-lg text-stone-light font-body max-w-2xl mx-auto mt-6">
            Ready to sell, pawn, or just curious about an item? 
            Stop by our shop or give us a call!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            {/* Phone */}
            <a
              href="tel:850-615-6105"
              data-testid="contact-phone-link"
              className="stone-card flex items-center gap-5 bg-bone-white/10 border-3 border-bone-white/20 rounded-rock-lg p-5 hover:bg-bone-white/15 transition-colors group"
            >
              <div className="w-14 h-14 bg-magma-red rounded-full flex items-center justify-center shadow-deep pulse-phone shrink-0">
                <Phone size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-heading text-lg text-accent uppercase">Call Us</h3>
                <p className="text-xl md:text-2xl font-body font-bold text-bone-white">850-615-6105</p>
              </div>
            </a>

            {/* Hours */}
            <div
              data-testid="contact-hours"
              className="bg-bone-white/10 border-3 border-bone-white/20 rounded-rock-lg p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-jungle-green rounded-full flex items-center justify-center">
                  <Clock size={18} className="text-white" />
                </div>
                <h3 className="font-heading text-lg text-accent uppercase">Business Hours</h3>
              </div>
              <div className="space-y-2 text-stone-light font-body">
                <div className="flex justify-between border-b border-bone-white/10 pb-2">
                  <span>Monday - Friday</span>
                  <span className="font-semibold text-bone-white">10:00 AM - 5:00 PM</span>
                </div>
                <div className="flex justify-between border-b border-bone-white/10 pb-2">
                  <span>Saturday</span>
                  <span className="font-semibold text-bone-white">10:00 AM - 2:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="font-semibold text-magma-red">Closed</span>
                </div>
              </div>
            </div>

            {/* Address */}
            <div
              data-testid="contact-address"
              className="bg-bone-white/10 border-3 border-bone-white/20 rounded-rock-lg p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                  <MapPin size={18} className="text-dirt-brown" />
                </div>
                <h3 className="font-heading text-lg text-accent uppercase">Our Location</h3>
              </div>
              <p className="text-lg text-bone-white font-body font-bold mb-1">
                4417 Jackson Street
              </p>
              <p className="text-base text-stone-light font-body mb-4">
                Marianna, Florida 32448
              </p>
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-btn"
                className="cave-btn inline-flex items-center gap-2 px-6 py-3 bg-accent text-dirt-brown font-heading text-sm uppercase tracking-wider rounded-rock shadow-soft hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all duration-300"
              >
                <Navigation size={16} />
                Get Directions
              </a>
            </div>
          </div>

          {/* Embedded Map */}
          <div
            data-testid="contact-map"
            className="rounded-rock-lg overflow-hidden border-4 border-bone-white/20 shadow-deep min-h-[350px] md:min-h-[450px]"
          >
            <iframe
              title="Cave City Pawn Shop Location - 4417 Jackson Street, Marianna, FL 32448"
              src={MAP_EMBED}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '350px', height: '100%' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
